import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Alert, Button, Card, Input, InputNumber, message, Select, Space, Table, Typography } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { PageHeader } from "../../components/PageHeader";
import { DocumentStatusTag } from "../../components/DocumentStatusTag";
import { ApprovalTimeline } from "../../components/ApprovalTimeline";
import { ProjectLink } from "../../components/ProjectLink";
import { useApi } from "../../helpers/useApi";
import { fmtItemFields } from "../../helpers/format";
import { useItemsQuery, useProjectsQuery } from "../../helpers/queries";
import type { BOMActivity, BOMVariation, ProjectBOMVersion } from "../../api-models";

interface DraftLine {
  key: string;
  bomItemId?: number;
  // Required when bomItemId is empty: a brand-new material still belongs to a piece of work.
  activityId?: number;
  itemId?: number;
  deltaQty?: number;
  notes?: string;
}

// flattenActivities turns the version's tree into an ordered flat list of
// (id, path) pairs — for the activity picker a new item needs, and to label the
// existing-item options so two rows for the same material can be told apart.
function flattenActivities(nodes: BOMActivity[] = [], prefix = "", into: { id: number; path: string }[] = []) {
  for (const n of nodes) {
    const path = prefix ? `${prefix} > ${n.name}` : n.name;
    into.push({ id: n.id, path });
    flattenActivities(n.children ?? [], path, into);
  }
  return into;
}

interface ImpactRow {
  bomItemId: number | null;
  itemId: number;
  itemCode: string;
  itemName: string;
  currentApproved: number;
  newApproved: number;
  committed: number;
  wouldViolate: boolean;
  shortfall: number;
  blockingRequests: string[];
}

// A BOM variation mutates the active version's approved quantities (spec §8) instead of
// editing it directly — every line here is a signed delta against an existing BOM item,
// or (no bomItemId) a brand-new material being added to the BOM under a named activity.
export default function BOMVariationPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { request, loading } = useApi();
  const { data: items = [] } = useItemsQuery();
  const { data: projects = [] } = useProjectsQuery();

  const isNew = id === "new";
  const projectId = isNew ? Number(searchParams.get("projectId")) : undefined;
  const [activeVersion, setActiveVersion] = useState<ProjectBOMVersion | null>(null);
  const [variation, setVariation] = useState<BOMVariation | null>(null);
  const [reason, setReason] = useState("");
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [impact, setImpact] = useState<ImpactRow[] | null>(null);

  useEffect(() => {
    if (isNew && projectId) {
      request<{ data: ProjectBOMVersion[] }>({ url: "/bom-versions", params: { projectId, status: "APPROVED_ACTIVE" } }).then(async (res) => {
        const active = res.data[0];
        if (active) setActiveVersion(await request<ProjectBOMVersion>({ url: `/bom-versions/${active.id}` }));
      });
    } else if (!isNew) {
      request<BOMVariation>({ url: `/bom-variations/${id}` }).then((v) => {
        setVariation(v);
        setReason(v.reason);
        setLines(
          (v.lines ?? []).map((l) => ({
            key: String(l.id),
            bomItemId: l.bomItemId ?? undefined,
            activityId: l.activityId ?? undefined,
            itemId: l.itemId,
            deltaQty: l.deltaQty,
            notes: l.notes,
          }))
        );
      });
      request<ImpactRow[]>({ url: `/bom-variations/${id}/impact` }, { silent: true }).then(setImpact).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const project = projects.find((p) => p.id === (projectId ?? variation?.projectId));
  const editable = isNew || variation?.status === "DRAFT";

  function addLine() {
    setLines([...lines, { key: `new-${Date.now()}` }]);
  }
  function updateLine(key: string, patch: Partial<DraftLine>) {
    setLines(lines.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  }
  function removeLine(key: string) {
    setLines(lines.filter((l) => l.key !== key));
  }

  async function save() {
    const payload = {
      projectId: projectId ?? variation!.projectId,
      reason,
      lines: lines
        .filter((l) => l.deltaQty)
        .map((l) => ({
          bomItemId: l.bomItemId,
          // Only meaningful for a new material; the server ignores it otherwise.
          activityId: l.bomItemId ? undefined : l.activityId,
          itemId: l.itemId,
          deltaQty: l.deltaQty,
          notes: l.notes,
        })),
    };
    const created = await request<BOMVariation>({ method: "POST", url: "/bom-variations", data: payload });
    message.success("Variation saved as draft.");
    navigate(`/bom-variations/${created.id}`, { replace: true });
  }

  async function submit() {
    await request({ method: "POST", url: `/bom-variations/${id}/submit` });
    message.success("Submitted for approval.");
    const v = await request<BOMVariation>({ url: `/bom-variations/${id}` });
    setVariation(v);
  }

  if (!isNew && !variation) return null;

  const activityOptions = flattenActivities(activeVersion?.activities ?? []);
  const activityPathById = new Map(activityOptions.map((a) => [a.id, a.path]));

  // The ledger is on the item row itself now, so the approved figure is the row's own
  // arithmetic; the activity path disambiguates a material that appears under several.
  const bomItemOptions = (activeVersion?.items ?? [])
    .filter((l) => l.mapping === "MAPPED")
    .map((l) => ({
      value: l.id,
      label: `${items.find((it) => it.id === l.itemId)?.code ?? l.itemId} — ${activityPathById.get(l.activityId) ?? "Unassigned"} (approved ${
        l.approvedQty + l.variationQty - l.reductionQty
      })`,
      itemId: l.itemId,
    }));

  return (
    <div>
      <PageHeader
        title={isNew ? "New BOM Variation" : variation!.docNo}
        subtitle={<ProjectLink project={project} />}
        extra={
          <Space>
            {variation && (
              <span data-tour="doc-status">
                <DocumentStatusTag status={variation.status} />
              </span>
            )}
            {editable && (
              <Button type="primary" onClick={save} loading={loading} data-tour="variation-save">
                Save
              </Button>
            )}
            {!isNew && variation?.status === "DRAFT" && (
              <Button onClick={submit} loading={loading} data-tour="variation-submit">
                Submit for approval
              </Button>
            )}
          </Space>
        }
      />
      <Card size="small" title="Reason" style={{ marginBottom: 16 }}>
        <Input.TextArea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} disabled={!editable} data-tour="variation-reason" />
      </Card>
      <Card size="small" title="Lines" style={{ marginBottom: 16 }} data-tour="variation-lines">
        <Table
          rowKey="key"
          size="small"
          pagination={false}
          dataSource={lines}
          columns={[
            {
              title: "Existing BOM item (or leave blank to add a new item)",
              width: 320,
              render: (_: unknown, row: DraftLine) =>
                editable ? (
                  <Select
                    allowClear
                    style={{ width: "100%" }}
                    placeholder="New item"
                    value={row.bomItemId}
                    options={bomItemOptions}
                    onChange={(v, opt) => updateLine(row.key, { bomItemId: v, itemId: (opt as { itemId?: number })?.itemId })}
                  />
                ) : (
                  items.find((it) => it.id === row.itemId)?.name
                ),
            },
            {
              title: "New item",
              width: 260,
              render: (_: unknown, row: DraftLine) =>
                editable && !row.bomItemId ? (
                  <Select
                    showSearch
                    style={{ width: "100%" }}
                    placeholder="Select item"
                    value={row.itemId}
                    options={items.map((it) => ({ value: it.id, label: `${it.code} — ${it.name}` }))}
                    filterOption={(input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase())}
                    onChange={(v) => updateLine(row.key, { itemId: v })}
                  />
                ) : null,
            },
            {
              // A new material has to land somewhere in the work breakdown — an existing
              // item already knows its activity, so the picker only appears for new ones.
              title: "Activity",
              width: 240,
              render: (_: unknown, row: DraftLine) => {
                if (row.bomItemId) return activityPathById.get(row.activityId ?? -1) ?? "—";
                if (!editable) return activityPathById.get(row.activityId ?? -1) ?? "General";
                return (
                  <Select
                    showSearch
                    allowClear
                    style={{ width: "100%" }}
                    placeholder="General"
                    value={row.activityId}
                    options={activityOptions.map((a) => ({ value: a.id, label: a.path }))}
                    filterOption={(input, option) => String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                    onChange={(v) => updateLine(row.key, { activityId: v ?? undefined })}
                  />
                );
              },
            },
            {
              title: "Delta qty (+/-)",
              width: 140,
              render: (_: unknown, row: DraftLine) =>
                editable ? (
                  <InputNumber value={row.deltaQty} onChange={(v) => updateLine(row.key, { deltaQty: v ?? undefined })} style={{ width: "100%" }} />
                ) : (
                  row.deltaQty
                ),
            },
            {
              title: "Notes",
              render: (_: unknown, row: DraftLine) =>
                editable ? <Input value={row.notes} onChange={(e) => updateLine(row.key, { notes: e.target.value })} /> : row.notes,
            },
            ...(editable
              ? [
                  {
                    title: "",
                    key: "remove",
                    width: 48,
                    render: (_: unknown, row: DraftLine) => <Button icon={<DeleteOutlined />} danger type="text" onClick={() => removeLine(row.key)} />,
                  },
                ]
              : []),
          ]}
        />
        {editable && (
          <Button icon={<PlusOutlined />} onClick={addLine} style={{ marginTop: 8 }} data-tour="variation-add-line">
            Add line
          </Button>
        )}
      </Card>
      {impact && impact.length > 0 && (
        <Card size="small" title="Impact" style={{ marginBottom: 16 }} data-tour="variation-impact">
          {impact.map((row, i) => (
            <div key={i} style={{ marginBottom: 8 }}>
              {row.wouldViolate ? (
                <Alert
                  type="error"
                  showIcon
                  message={`${fmtItemFields(row.itemCode, row.itemName)}: new approved qty ${row.newApproved} would fall below ${row.committed} already committed (shortfall ${row.shortfall}).`}
                  description={row.blockingRequests.length ? `Blocking requests: ${row.blockingRequests.join(", ")}` : undefined}
                />
              ) : (
                <Typography.Text type="secondary">
                  {fmtItemFields(row.itemCode, row.itemName)}: {row.currentApproved} → {row.newApproved}
                </Typography.Text>
              )}
            </div>
          ))}
        </Card>
      )}
      {!isNew && (
        <ApprovalTimeline
          docType="BOM_VARIATION"
          docId={Number(id)}
          refreshKey={variation?.status}
          onActed={() => request<BOMVariation>({ url: `/bom-variations/${id}` }).then(setVariation)}
        />
      )}
    </div>
  );
}
